import "./interfaces.d.ts";
import { BrowserStorage } from "utils/BrowserStorage";
import { CoreStore, handleStoreState } from "Stores/RESTStore/CoreStore";
import { observable, action, computed, makeObservable } from "mobx";
import { projectsDataStore, userInfoStore } from "Stores";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { download, fileFromStreamedPayload, StreamedFilePayload } from "utils/download";
import { actionProgressStore } from "Components/ActionProgress/ActionProgressStore";
import { CollectionFolderFile, CollectionFolderTag } from "./interfaces";
import { modal } from "Components/General/Modal/AntdModal";
import i18n, { I18N_NS } from "Config/i18n";

const COLLECTION_FOLDER_FILES = "ColFolderIds";

interface CollectionFolderFileData {
  id: number;
  tag?: CollectionFolderTag;
  state: API_FileDataEntity["state"];
}

export class CollectionFolderStore extends CoreStore {
  data: CollectionFolderFile[] = [];
  fileIds: CollectionFolderFileData[] = [];
  isVisible = false;
  accessRevokedFiles: number[] = [];
  showProjectFiles = false;

  constructor() {
    super();

    makeObservable(this, {
      data: observable.shallow,
      fileIds: observable.shallow,
      isVisible: observable,
      accessRevokedFiles: observable,
      showProjectFiles: observable,
      clear: action.bound,
      init: action.bound,
      addFile: action.bound,
      removeFile: action.bound,
      removeRevokedFiles: action.bound,
      setFiles: action.bound,
      addFiles: action.bound,
      clearAll: action.bound,
      toggleModal: action.bound,
      toggleProjectFiles: action.bound,
      exportFilesAsExcel: action.bound,
      downloadSelection: action.bound,
      setRevokedFiles: action.bound,
      idsNamespace: computed,
      fetch: action.bound,
    });
  }

  url() {
    return API_URL.FILES.getRefreshedFiles_1(projectsDataStore.projectId);
  }

  clear() {
    this.data = [];
    this.fileIds = [];
    this.isVisible = false;
    this.showProjectFiles = false;
    this.accessRevokedFiles = [];
  }

  async init() {
    try {
      const persistedFileIds = BrowserStorage.getPermanent(this.idsNamespace);
      this.data = [];
      this.fileIds = persistedFileIds || [];
      // fetch files data for persisted files
      if (persistedFileIds && persistedFileIds.length) {
        await this.fetch();
      }
    } catch (error) {
      throw error;
    }
  }

  addFile(value: CollectionFolderFile) {
    this.addFiles([value]);
  }

  removeFile(id: number) {
    const newFiles = this.data.filter((item) => item.id !== id);
    this.setFiles(newFiles);
  }

  removeRevokedFiles(fileIds: number[]) {
    const newFileIds = this.fileIds.filter((item) => !fileIds.includes(item.id));
    fileIds.forEach((f) => this.removeFile(f));
    this.fileIds = newFileIds;
    this.fetch();
  }

  setFiles(values: CollectionFolderFile[]) {
    this.data = values;

    this.fileIds = values.map((item) => {
      if (item.state === "SYMLINK") {
        return { id: item.linkId, tag: item.tag, state: item.state || "SYMLINK" };
      } else {
        return { id: item.id, tag: item.tag, state: item.state || "ACTIVE" };
      }
    });
    this._saveData();
  }

  /** This function is needed because in Collection Folder we have a "Label" feature.
   * Label - is a "tag" property that is handled on front-end side to categorize files in collection folder.
   * In order not to loose this "tag" property when receiving new values from project files table
   * (which are preselected in order to show that these files are already included in the collection folder)
   * this function omits from resetting of object without "tag" property.
   */
  addFiles(values: CollectionFolderFile[]) {
    const initial = this.data.slice();
    const withDubs = initial.concat(values);

    const buffer = {};
    withDubs.forEach((item) => {
      // if property does not exist in buffer - add it, otherwise skip it
      if (!buffer[item.id]) {
        buffer[item.id] = item;
      }
    });

    this.setFiles(Object.values(buffer));
  }

  clearAll() {
    this.setFiles([]);
    this.accessRevokedFiles = [];
  }

  toggleModal() {
    this.isVisible = !this.isVisible;
    if (!this.isVisible) {
      this.showProjectFiles = false;
    } else if (this.fileIds.length) {
      this.fetch();
    }
  }

  toggleProjectFiles() {
    this.showProjectFiles = !this.showProjectFiles;
  }

  @handleStoreState()
  async fetch() {
    const fileIds = this.fileIds.filter((item) => !!item); // filter possible null values;
    if (fileIds.length) {
      try {
        const formBody: API_MultipleFileRequest = {
          files: fileIds.map((item) => ({ id: item.id, state: item.state || "ACTIVE" })),
        };
        const response = await API.newData<API_FileDataEntity[]>(this.url(), formBody);

        const filesTagsMap: { [key: number]: CollectionFolderTag } = {};
        fileIds.forEach((item) => {
          filesTagsMap[item.id] = item.tag;
        });
        const mergedWithTags = (response as CollectionFolderFile[]).reduce((buffer, current) => {
          if (current.state === "DELETED") {
            current.tag = "DELETED";
          } else if (current.properties.accessright === "1W") {
            current.tag = "RWM";
          } else if (filesTagsMap[current.id]) {
            current.tag = filesTagsMap[current.id];
          }
          if (current.state === "ACTIVE" && current.tag === "DELETED") {
            delete current.tag;
          }
          if (current) {
            buffer.push(current);
          } else {
            console.warn(
              "%c Empty response object! Check " + this.url(),
              "font-weight: bold; font-size: 18px; color: red"
            );
          }
          return buffer;
        }, []);
        this.setFiles(mergedWithTags as CollectionFolderFile[]);
      } catch (error) {
        if (collectionFolderStore.isVisible && error.response && error.response.data.code === 1070) {
          modal.warning({
            title: i18n.t("Files access revoked", { ns: I18N_NS.PROJECT }) as string,
            content: i18n.t(
              `{{amount}} files must automatically be removed from your collection folder because your access to these was revoked.`,
              { ns: I18N_NS.PROJECT, amount: error.response.data.fileIds.length }
            ) as string,
            okText: i18n.t("OK") as string,
            okType: "primary",
            zIndex: 999999,
            onOk: () => {
              this.removeRevokedFiles(error.response.data.fileIds);
            },
          });
        }
        throw error;
      }
    }
  }

  async exportFilesAsExcel(fileIds: number[]) {
    try {
      const formBody: API_MultipleFileRequest = this._prepareFormBody(fileIds);
      const progressId = actionProgressStore.init();
      try {
        const payload = await API.newData<StreamedFilePayload>(
          API_URL.FILES.getExcelFileList(projectsDataStore.projectId),
          formBody,
          null,
          {
            cancelToken: actionProgressStore.getCancelToken(progressId),
            onProgress: (percent) => actionProgressStore.setPercent(progressId, percent),
          }
        );
        const file = fileFromStreamedPayload(payload);
        download(file, file.name, file.type);
      } finally {
        actionProgressStore.dismiss(progressId);
      }
    } catch (error) {
      throw error;
    }
  }

  async downloadSelection(fileIds: number[], withDirectories: boolean) {
    if (!fileIds || !fileIds.length) {
      return;
    }
    try {
      const formBody: API_MultipleFilesWithFoldersRequest = this._prepareFormBody(fileIds);
      formBody.withFolders = withDirectories;
      const file = await API.getBlobByPost(API_URL.FILES.getFiles(projectsDataStore.projectId), formBody);
      download(file, file.name, file.type);
    } catch (error) {
      throw error;
    }
  }

  setRevokedFiles(files: number[] = []) {
    this.accessRevokedFiles = files;
  }

  get idsNamespace() {
    return `${COLLECTION_FOLDER_FILES}_${userInfoStore.id}_${projectsDataStore.projectId}`;
  }

  private _saveData() {
    const fileIds = this.fileIds.slice();
    BrowserStorage.storePermanent(this.idsNamespace, fileIds.length ? fileIds : null);
  }

  private _prepareFormBody(fileIds: number[]): API_MultipleFileRequest {
    const files: API_MultipleFileRequest["files"] = fileIds.map((fileId) => {
      const fileFound = this.data.find((item) => item.id === fileId);
      if (fileFound.state === "SYMLINK") {
        return { id: fileFound.linkId, state: "SYMLINK" };
      } else {
        return { id: fileId, state: fileFound.state };
      }
    });
    return { files };
  }
}

export const collectionFolderStore = new CollectionFolderStore();

if (process.env.NODE_ENV === "development") {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).collectionFolder = collectionFolderStore;
}
